/**
 * Quiz Game Module
 * "Wiedza o marce" — quiz z 10 pytaniami tekstowymi.
 *
 * Zarządza trzema sekcjami (hero+FAQ zawsze widoczne):
 *   #quizIntro — strona startowa (points bar, illustration, CTA)
 *   #quizBoard — ekran pytania (progress, odpowiedzi, nawigacja)
 *   #quizEnd   — ekran końcowy z wynikami
 *
 * Pytania ładowane z JSON: data/quiz-questions.json
 * Inicjalizacja automatyczna — szuka #quizIntro na stronie.
 */

(function () {
  'use strict';

  // ==================== CONFIG ====================

  var DATA_URL = 'data/quiz-questions.json';
  var TRANSITION_DURATION = 400;
  var ANIM_DURATION = 300; // slide animation between questions

  // ==================== DOM REFS ====================

  var introSection, boardSection, endSection;
  var startBtn, playAgainBtn;
  var boardArea;
  var correctAnswersEl, totalQuestionsEl, quizEndHeading;

  // ==================== STATE ====================

  var questions = [];
  var currentIndex = 0;
  var userAnswers = []; // stores selected answer index per question (-1 = unanswered)
  var isFirstShow = true;
  var isAnimating = false; // prevents double-clicks during slide animation

  // ==================== SECTION SWITCHING ====================

  function showSection(name) {
    var sections = {
      intro: introSection,
      board: boardSection,
      end: endSection
    };

    if (isFirstShow) {
      isFirstShow = false;
      Object.keys(sections).forEach(function (key) {
        var el = sections[key];
        if (!el) return;
        if (key === name) {
          el.style.display = '';
          el.classList.add('quiz-section--active');
        } else {
          el.style.display = 'none';
          el.classList.remove('quiz-section--active');
        }
      });
      return;
    }

    var currentSection = null;
    Object.keys(sections).forEach(function (key) {
      var el = sections[key];
      if (el && el.classList.contains('quiz-section--active')) {
        currentSection = el;
      }
    });

    var targetSection = sections[name];

    function fadeIn() {
      if (!targetSection) return;
      targetSection.style.display = '';
      void targetSection.offsetHeight;
      targetSection.classList.add('quiz-section--active');
    }

    if (currentSection && currentSection !== targetSection) {
      currentSection.classList.remove('quiz-section--active');
      setTimeout(function () {
        currentSection.style.display = 'none';
        fadeIn();
      }, TRANSITION_DURATION);
    } else {
      fadeIn();
    }
  }

  // ==================== DATA LOADING ====================

  function loadQuestions(callback) {
    fetch(DATA_URL)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        questions = data;
        callback(null);
      })
      .catch(function (err) {
        console.error('[Quiz] Failed to load questions:', err);
        callback(err);
      });
  }

  // ==================== RENDERING ====================

  function renderQuestion() {
    if (!boardArea || !questions.length) return;

    var q = questions[currentIndex];
    var total = questions.length;
    var progressPercent = ((currentIndex + 1) / total) * 100;
    var isFirst = currentIndex === 0;
    var selectedAnswer = userAnswers[currentIndex];

    // Build checkbox SVG — Camel Blue
    var checkSvg =
      '<svg width="49" height="49" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<circle cx="24.3935" cy="23.2324" r="16.2626" fill="#006cff"/>' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M32.6829 17.6873C32.9149 17.4573 33.2882 17.4601 33.5168 17.6935C33.7409 17.9223 33.7426 18.2878 33.524 18.5188L33.5107 18.5325L20.8364 31.0994C20.6101 31.3237 20.2491 31.3268 20.0192 31.1097L20.0055 31.0964L15.2733 26.3349C15.043 26.1032 15.043 25.7275 15.2733 25.4958C15.4991 25.2686 15.8623 25.2642 16.0935 25.4824L16.1072 25.4958L20.4255 29.8408L32.6829 17.6873Z" fill="#E6E7E8"/>' +
      '</svg>';

    // Back button SVG
    var backArrowSvg =
      '<svg class="quiz-question__back-icon" width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<circle cx="12.5" cy="12.5" r="12" stroke="currentColor" stroke-opacity="0.3"/>' +
      '<path d="M14 8L10 12.5L14 17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';

    var html = '';

    // ---- Outer content wrapper (constrains width on desktop) ----
    html += '<div class="quiz-question__content">';

    // ---- Back button ----
    html += '<div class="quiz-question__nav">';
    html += '<button class="quiz-question__back' + (isFirst ? ' quiz-question__back--disabled' : '') + '" type="button"' + (isFirst ? ' disabled' : '') + '>';
    html += backArrowSvg;
    html += '<span class="quiz-question__back-label">Poprzednie pytanie</span>';
    html += '</button>';
    html += '</div>';

    // ---- Progress ----
    html += '<div class="quiz-question__progress">';
    html += '<p class="quiz-question__progress-label">PYTANIE ' + (currentIndex + 1) + ' / ' + total + '</p>';
    html += '<div class="quiz-question__progress-track">';
    html += '<div class="quiz-question__progress-fill" style="width:' + progressPercent + '%"></div>';
    html += '</div>';
    html += '</div>';

    // ---- Category ----
    html += '<p class="quiz-question__category">' + q.category + '</p>';

    // ---- Question text ----
    html += '<h3 class="quiz-question__text">' + q.question + '</h3>';

    // ---- Answers ----
    html += '<div class="quiz-question__answers">';
    for (var i = 0; i < q.answers.length; i++) {
      var isSelected = selectedAnswer === i;
      html += '<button class="quiz-answer' + (isSelected ? ' quiz-answer--selected' : '') + '" type="button" data-index="' + i + '">';
      html += '<span class="quiz-answer__text">' + q.answers[i] + '</span>';
      html += '<span class="quiz-answer__checkbox">' + (isSelected ? checkSvg : '') + '</span>';
      html += '</button>';
    }
    html += '</div>';

    // ---- Submit button ----
    var isDisabled = selectedAnswer === -1 || selectedAnswer === undefined;
    html += '<div class="quiz-question__actions">';
    html += '<button class="quiz-question__submit' + (isDisabled ? ' quiz-question__submit--disabled' : '') + '" type="button"' + (isDisabled ? ' disabled' : '') + '>';
    html += 'Dalej';
    html += '</button>';
    html += '</div>';

    // ---- Close content wrapper ----
    html += '</div>';

    boardArea.innerHTML = html;

    // ---- Bind events ----
    bindQuestionEvents();
  }

  // ==================== EVENT BINDING ====================

  function bindQuestionEvents() {
    if (!boardArea) return;

    // Answer buttons
    var answerBtns = boardArea.querySelectorAll('.quiz-answer');
    for (var i = 0; i < answerBtns.length; i++) {
      answerBtns[i].addEventListener('click', onAnswerClick);
    }

    // Back button
    var backBtn = boardArea.querySelector('.quiz-question__back');
    if (backBtn && !backBtn.disabled) {
      backBtn.addEventListener('click', onBackClick);
    }

    // Submit button
    var submitBtn = boardArea.querySelector('.quiz-question__submit');
    if (submitBtn && !submitBtn.disabled) {
      submitBtn.addEventListener('click', onSubmitClick);
    }
  }

  function onAnswerClick(e) {
    var btn = e.currentTarget;
    var index = parseInt(btn.getAttribute('data-index'), 10);

    userAnswers[currentIndex] = index;
    renderQuestion();
  }

  function onBackClick() {
    if (currentIndex > 0 && !isAnimating) {
      animateToQuestion(currentIndex - 1, 'back');
    }
  }

  function onSubmitClick() {
    if (userAnswers[currentIndex] === -1 || userAnswers[currentIndex] === undefined) return;
    if (isAnimating) return;

    if (currentIndex < questions.length - 1) {
      // Next question
      animateToQuestion(currentIndex + 1, 'forward');
    } else {
      // Quiz finished
      showEndScreen();
    }
  }

  // ==================== QUESTION ANIMATION ====================

  /**
   * Animate transition between questions.
   * @param {number} nextIndex — index of the target question
   * @param {'forward'|'back'} direction — navigation direction
   */
  function animateToQuestion(nextIndex, direction) {
    isAnimating = true;

    var contentEl = boardArea.querySelector('.quiz-question__content');
    if (!contentEl) {
      // Fallback — no animation
      currentIndex = nextIndex;
      renderQuestion();
      isAnimating = false;
      return;
    }

    // Capture current height and lock it
    var oldHeight = boardArea.offsetHeight;
    boardArea.style.height = oldHeight + 'px';

    // Determine slide-out and slide-in classes based on direction
    var slideOutClass = direction === 'forward'
      ? 'quiz-question__content--slide-out-left'
      : 'quiz-question__content--slide-out-right';
    var slideInClass = direction === 'forward'
      ? 'quiz-question__content--slide-in-right'
      : 'quiz-question__content--slide-in-left';

    // Phase 1: Slide out current content
    contentEl.classList.add(slideOutClass);

    setTimeout(function () {
      // Phase 2: Update index, render new content
      currentIndex = nextIndex;
      renderQuestion();

      // Phase 3: Measure new natural height
      boardArea.style.height = 'auto';
      var newHeight = boardArea.offsetHeight;

      // Phase 4: Reset to old height, then animate to new height
      boardArea.style.height = oldHeight + 'px';
      void boardArea.offsetHeight; // force reflow

      boardArea.style.height = newHeight + 'px';

      // Phase 5: Position new content off-screen and slide in
      var newContentEl = boardArea.querySelector('.quiz-question__content');
      if (newContentEl) {
        newContentEl.classList.add(slideInClass);

        // Force reflow so the browser registers the off-screen position
        void newContentEl.offsetHeight;

        // Remove the slide-in class to trigger transition to default state
        newContentEl.classList.remove(slideInClass);

        setTimeout(function () {
          // Clear explicit height so container is auto-sized again
          boardArea.style.height = '';
          isAnimating = false;
        }, ANIM_DURATION);
      } else {
        boardArea.style.height = '';
        isAnimating = false;
      }
    }, ANIM_DURATION);
  }

  // ==================== END SCREEN ====================

  function showEndScreen() {
    var correctCount = 0;
    for (var i = 0; i < questions.length; i++) {
      if (userAnswers[i] === questions[i].correct) {
        correctCount++;
      }
    }

    // Dynamic heading based on score
    var heading = 'NIEZŁA PRÓBA!';
    if (correctCount >= 8) {
      heading = 'PRAWDZIWY EKSPERT!';
    } else if (correctCount >= 5) {
      heading = 'MASZ NIEZŁĄ WIEDZĘ!';
    }

    if (quizEndHeading) quizEndHeading.textContent = heading;
    if (correctAnswersEl) correctAnswersEl.textContent = correctCount;
    if (totalQuestionsEl) totalQuestionsEl.textContent = questions.length;

    showSection('end');

    // Show points modal after delay
    setTimeout(function () {
      if (typeof Modal !== 'undefined' && Modal.show) {
        Modal.show({
          variant: 'points',
          image: 'assets/images/own-it-card-big.jpg',
          pointsNumber: '235',
          pointsLabel: 'Punktów',
          text: '<strong>Gratulacje!</strong><br/>Za rozwiązanie quizu otrzymujesz:',
          value: '+ 50 PUNKTÓW',
          confirmLabel: 'Zamknij'
        });
      }
    }, 3000);
  }

  // ==================== RESET ====================

  function resetQuiz() {
    currentIndex = 0;
    userAnswers = [];
    for (var i = 0; i < questions.length; i++) {
      userAnswers.push(-1);
    }
  }

  // ==================== INIT ====================

  function init() {
    introSection = document.getElementById('quizIntro');
    boardSection = document.getElementById('quizBoard');
    endSection = document.getElementById('quizEnd');

    // Only init if quiz page elements exist
    if (!introSection && !boardSection) return;

    boardArea = document.getElementById('quizBoardArea');
    correctAnswersEl = document.getElementById('quizCorrectAnswers');
    totalQuestionsEl = document.getElementById('quizTotalQuestions');
    quizEndHeading = document.getElementById('quizEndHeading');

    // Preload questions
    loadQuestions(function (err) {
      if (err) return;

      // Init userAnswers array
      resetQuiz();

      // Start button — switch from intro to board
      startBtn = document.getElementById('quizStartBtn');
      if (startBtn) {
        startBtn.addEventListener('click', function () {
          resetQuiz();
          renderQuestion();

          // Position content off-screen before the section becomes visible
          var contentEl = boardArea ? boardArea.querySelector('.quiz-question__content') : null;
          if (contentEl) {
            contentEl.style.opacity = '0';
            contentEl.style.transform = 'translateX(40px)';
            contentEl.style.transition = 'none';
          }

          showSection('board');

          // After section fade begins, animate the content sliding in
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              if (contentEl) {
                contentEl.style.transition = '';
                contentEl.style.opacity = '';
                contentEl.style.transform = '';
              }
            });
          });
        });
      }

      // Play again button — switch from end to board
      playAgainBtn = document.getElementById('quizPlayAgain');
      if (playAgainBtn) {
        playAgainBtn.addEventListener('click', function () {
          resetQuiz();
          showSection('board');
          renderQuestion();
        });
      }
    });

    // DEV toolbar — only on localhost
    if (window.location.hostname === 'localhost' || window.location.hostname === 'camel2dev.polymus.info.pl' || window.location.hostname === '127.0.0.1') {
      createDevToolbar();
    }
  }

  // ==================== DEV TOOLBAR ====================

  function createDevToolbar() {
    var toolbar = document.createElement('div');
    toolbar.style.cssText =
      'position:fixed;bottom:16px;right:16px;z-index:9999;display:flex;gap:6px;' +
      'background:rgba(0,0,0,0.85);padding:8px 12px;border-radius:8px;font-family:monospace;font-size:12px;';

    var screens = [
      { label: '① Intro', view: 'intro' },
      { label: '② Board', view: 'board' },
      { label: '③ End', view: 'end' }
    ];

    screens.forEach(function (s) {
      var btn = document.createElement('button');
      btn.textContent = s.label;
      btn.style.cssText =
        'background:#333;color:#fff;border:1px solid #555;padding:4px 10px;border-radius:4px;' +
        'cursor:pointer;font-family:inherit;font-size:12px;';
      btn.addEventListener('mouseenter', function () { btn.style.background = '#006cff'; });
      btn.addEventListener('mouseleave', function () { btn.style.background = '#333'; });
      btn.addEventListener('click', function () {
        if (s.view === 'board') {
          if (!questions.length) return;
          renderQuestion();
        }
        if (s.view === 'end') {
          showEndScreen();
          return; // showEndScreen already calls showSection('end')
        }
        showSection(s.view);
      });
      toolbar.appendChild(btn);
    });

    document.body.appendChild(toolbar);
  }

  // Auto-init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
